<template>
  <b-modal
    ref="deviceInstallModal"
    :id="id"
    :title="title"
    size="lg"
    :no-close-on-backdrop="true"
    :hide-footer="true"
    header-bg-variant="dark"
    header-text-variant="light"
    body-bg-variant="light"
    @hide="resetModal"
    @shown="initialiseModal"
    :no-fade="true"
    :static="true"
  >
    <b-container fluid>
      <form-wizard
        ref="deviceInstallFormWizard"
        title
        subtitle
        stepSize="sm"
        @on-complete="callInstallDevice"
        color="#eb5a5f"
        errorColor="#8a4500"
      >
        <tab-content title="LineSIGHT Details" :before-change="validateFirstTab">
          <b-alert
            variant="info"
            show
          >Please search for a LineSIGHT or if you cannot find the LineSIGHT device, please create a new one</b-alert>
          <b-alert
            variant="danger"
            dismissible
            :show="!stepOne.valid && this.inputsBothEmpty"
            @dismissed="stepOne.valid=true"
          >
            <span>
              You have not selected an existing LineSIGHT or input a new LineSIGHT
              serial to install.
            </span>
          </b-alert>
          <b-alert
            variant="danger"
            dismissible
            :show="this.showCreateDeviceError"
            @dismissed="stepOne.valid=true"
          >
            <span>
              You have entered an existing LineSIGHT. Please input a new LineSIGHT
              serial to install.
            </span>
          </b-alert>
          <b-alert
            variant="danger"
            dismissible
            :show="this.showExistDeviceError"
            @dismissed="stepOne.valid=true"
          >
            <span>
              You have entered a device serial into the Existing device search,
              please select the device from the list returned, if the device
              does not appear in the list, please create the device
            </span>
          </b-alert>
          <b-alert
            variant="danger"
            dismissible
            :show="!this.is_serial_valid"
            @dismissed="stepOne.valid=true"
          >
            <span>
              You have entered an invalid LineSIGHT serial number. Please input a
              valid serial to install.
            </span>
          </b-alert>
          <b-row class="row-divided">
            <b-col>
              <h5>Existing Device</h5>
              <vue-bootstrap-typeahead
                ref="deviceSearchTypeAhead"
                v-if="!deviceToInstall"
                :data="devicesNotInstalled"
                id="existingSerial"
                :serializer="d => d.product_serial_num"
                v-model="sentinelSearch"
                placeholder="LineSIGHT serial number"
                @keypress.native="isNumberOrHyphen"
                @hit="selectDevice"
                backgroundVariant="white"
                inputClass="deviceSearchInputTypeAhead"
                @input="handleAutocompleteInput"
                :class="{ existingSerial : this.showExistDeviceError }"
              >
                <template slot="suggestion" slot-scope="{ data, htmlText }">
                  <div class="d-flex align-items-center">
                    <!-- Note: the v-html binding is used, as htmlText contains the suggestion text highlighted with <strong> tags -->
                    <span class v-html="htmlText"></span>
                    <span
                      class="ml-auto text-right fs-sm"
                      v-if="data.pole"
                    >{{data.pole.pole_substation_number}} ({{data.pole.hv_pole_number}})</span>
                  </div>
                </template>
                <template slot="append">
                  <b-btn
                    size="sm"
                    id="clearExistedDeviceInputButton"
                    :disabled="isExistedDeviceInputDisabled"
                    variant="secondary"
                    @click.prevent="clearSentinelSearch"
                  >
                    <i class="far fa-times-circle fa-lg"></i>
                  </b-btn>
                </template>
              </vue-bootstrap-typeahead>
              <b-row
                v-if="sentinel.product_serial_num"
                class="mt-2"
              >
                <b-col>
                  <b-card>
                    <h4 class="card-title">{{sentinel.product_serial_num}}</h4>
                    <p class="card-text">
                      <strong>Last Communication:</strong>&nbsp;
                      <span
                        v-if="sentinel && sentinel.device_last_message_time"
                      >{{sentinel.device_last_message_time | formatDate}}</span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                    <p class="card-text">
                      <strong>Last Location:</strong>&nbsp;
                      <span v-if="sentinel && sentinel.last_location">
                        <b-link
                          class="card-link"
                          :href="deviceLocationUrl(sentinel.last_location.latitude, sentinel.last_location.longitude)"
                          target="_blank"
                        >
                          {{sentinel.last_location.latitude}},
                          {{sentinel.last_location.longitude}}
                        </b-link>
                      </span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                  </b-card>
                </b-col>
              </b-row>
            </b-col>
            <div class="vertical-divider">or</div>
            <b-col>
              <h5>Create New Device</h5>
              <b-input-group :class="{ newSentinelInput:  this.showCreateDeviceError }">
              <center>
                <div class="input-group">
                <select v-model="selected_prefix" class="form-control">
                    <option value="279-">279-</option>
                    <option value="162-">162-</option>
                  </select>
                <masked-input
                    id="newSentinelInput"
                    ref="newSentinelInput"
                    :disabled="!this.poleToInstallDevice || isNewDeviceInputDisabled"
                    class="form-control"
                    @input="selectNewDevice"
                    type="text"
                    placeholder="Serial Number"
                    mask="11111111"
                    :key="maskedComponentKey"
                ></masked-input>
                <b-input-group-append>
                  <b-btn
                    size="sm"
                    id="clearNewDeviceInputButton"
                    :disabled="!this.poleToInstallDevice || isNewDeviceInputDisabled"
                    variant="secondary"
                    @click.prevent="clearNewDeviceInput"
                  >
                    <i class="far fa-times-circle fa-lg"></i>
                  </b-btn>
                </b-input-group-append>
                </div>
                </center>
              </b-input-group>
            </b-col>
          </b-row>
        </tab-content>
        <tab-content title="Pole Information" :before-change="validateSecondTab">
          <b-alert
            variant="danger"
            dismissable
            :show="!stepTwo.valid"
          >Please search for a Pole or if you cannot find the correct Pole, please create a new pole and then return to install a LineSIGHT</b-alert>
          <b-row class="row-divided">
            <b-col>
              <h5>Existing Pole</h5>
              <vue-bootstrap-typeahead
                ref="poleSearchTypeAhead"
                v-if="!poleToInstallDevice"
                :data="are_devices_loaded ? polesWithNoDeviceInstalled : []"
                :serializer="d => d.pole_substation_number"
                v-model="poleSearch"
                placeholder="Enter Substation Number"
                @hit="selectPole"
                backgroundVariant="white"
                inputClass="poleSearchInputTypeAhead"
              >
                <template slot="suggestion" slot-scope="{ data, htmlText }">
                  <div class="d-flex align-items-center">
                    <!-- Note: the v-html binding is used, as htmlText contains the suggestion text highlighted with <strong> tags -->
                    <span class v-html="htmlText"></span>
                    <span class="ml-auto text-right fs-sm" v-if="data.device">{{data.device}}</span>
                  </div>
                </template>
                <template slot="append">
                  <b-btn size="sm" variant="secondary" @click.prevent="clearPoleSearch">
                    <i class="far fa-times-circle fa-lg"></i>
                  </b-btn>
                </template>
              </vue-bootstrap-typeahead>
              <b-row v-if="!isVariableEmpty(sentinel.pole)" class="mt-2">
                <b-col>
                  <b-card title="Pole Details">
                    <p class="card-text">
                      <strong>Substation Name:</strong>&nbsp;
                      <span
                        v-if="sentinel && sentinel.pole && sentinel.pole.pole_substation_name"
                      >{{sentinel.pole.pole_substation_name}}</span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                    <p class="card-text">
                      <strong>Substation Number:</strong>&nbsp;
                      <span
                        v-if="sentinel && sentinel.pole && sentinel.pole.pole_substation_number"
                      >{{sentinel.pole.pole_substation_number}}</span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                    <p class="card-text">
                      <strong>HV Pole Number:</strong>&nbsp;
                      <span
                        v-if="sentinel && sentinel.pole && sentinel.pole.hv_pole_number"
                      >{{sentinel.pole.hv_pole_number}}</span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                    <p class="card-text">
                      <strong>Coordinates:</strong>&nbsp;
                      <span v-if="sentinel && sentinel.pole && sentinel.pole.coordinates">
                        <b-link
                          class="card-link"
                          :href="deviceLocationUrl(sentinel.pole.coordinates.latitude, sentinel.pole.coordinates.longitude)"
                          target="_blank"
                        >
                          {{sentinel.pole.coordinates.latitude}},
                          {{sentinel.pole.coordinates.longitude}}
                        </b-link>
                      </span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                    <p class="card-text">
                      <strong>Notes:</strong>&nbsp;
                      <span
                        v-if="sentinel && sentinel.pole && sentinel.pole.notes"
                      >{{sentinel.pole.notes}}</span>
                      <span v-else class="font-italic">N/A</span>
                    </p>
                  </b-card>
                </b-col>
              </b-row>
            </b-col>
            <div class="vertical-divider" v-if="stepTwo.enableCreate">or</div>
            <b-col v-if="stepTwo.enableCreate">
              <h5>Create New Pole</h5>
              <b-form-row>
                <b-col>
                  <b-form-input
                    :disabled="!isVariableEmpty(sentinel.pole)"
                    v-model="newSentinel.pole.pole_substation_name"
                    type="text"
                    placeholder="Pole Name"
                  ></b-form-input>
                </b-col>
              </b-form-row>
              <b-form-row>
                <b-col>
                  <b-form-input
                    :disabled="!isVariableEmpty(sentinel.pole)"
                    v-model="newSentinel.pole.pole_substation_number"
                    type="text"
                    placeholder="Pole Location"
                  ></b-form-input>
                </b-col>
              </b-form-row>
              <b-form-row>
                <b-col>
                  <b-form-input
                    :disabled="!isVariableEmpty(sentinel.pole)"
                    v-model="newSentinel.pole.hv_pole_number"
                    type="text"
                    placeholder="Pole Number"
                  ></b-form-input>
                </b-col>
              </b-form-row>
              <b-form-row>
                <b-col>
                  <b-form-input
                    :disabled="!isVariableEmpty(sentinel.pole)"
                    v-model="newSentinel.pole.coordinates.latitude"
                    type="text"
                    placeholder="Pole Latitude"
                  ></b-form-input>
                </b-col>
                <b-col>
                  <b-form-input
                    :disabled="!isVariableEmpty(sentinel.pole)"
                    v-model="newSentinel.pole.coordinates.longitude"
                    type="text"
                    placeholder="Pole Longitude"
                  ></b-form-input>
                </b-col>
              </b-form-row>
              <b-form-row>
                <b-col>
                  <b-form-textarea
                    :disabled="!isVariableEmpty(sentinel.pole)"
                    v-model="newSentinel.pole.notes"
                    placeholder="Notes"
                    :rows="3"
                    no-resize
                  ></b-form-textarea>
                </b-col>
              </b-form-row>
            </b-col>
          </b-row>
        </tab-content>
        <tab-content title="Confirmation">
          <b-alert variant="info" show>
            Below are the details of where the LineSIGHT device will be installed.
            <br />Please confirm these details are correct before submitting the form
          </b-alert>
          <b-form-group label="LineSIGHT">
            <b-form-group label="Serial:" label-cols="2" label-for="sentinel-serial">
              <b-form-input
                v-if="!isVariableEmpty(sentinel.product_serial_num)"
                id="sentinel-serial"
                plaintext
                v-model="sentinel.product_serial_num"
              ></b-form-input>
              <b-form-input
                v-else
                id="sentinel-serial"
                plaintext
                v-model="newSentinel.product_serial_num"
              ></b-form-input>
            </b-form-group>
          </b-form-group>
          <b-form-group label="Pole" v-if="!isVariableEmpty(sentinel.pole)">
            <b-form-group label="Substation Name:" label-cols="2" label-for="pole-name">
              <b-form-input id="pole-name" plaintext v-model="sentinel.pole.pole_substation_name"></b-form-input>
            </b-form-group>
            <b-form-group label="Substation Number:" label-cols="2" label-for="pole-location">
              <b-form-input id="pole-location" plaintext v-model="sentinel.pole.pole_substation_number"></b-form-input>
            </b-form-group>
            <b-form-group label="HV Pole Number:" label-cols="2" label-for="pole-number">
              <b-form-input id="pole-number" plaintext v-model="sentinel.pole.hv_pole_number"></b-form-input>
            </b-form-group>
            <b-form-group label="Coordinates:" label-cols="2" label-for="pole-coordinates">
              <b-link
                v-if="!isVariableEmpty(sentinel.pole.coordinates)"
                id="pole-coordinates"
                class="form-control-plaintext"
                :href="deviceLocationUrl(sentinel.pole.coordinates.latitude, sentinel.pole.coordinates.longitude)"
                target="_blank"
              >
                {{sentinel.pole.coordinates.latitude}},
                {{sentinel.pole.coordinates.longitude}}
              </b-link>
            </b-form-group>
            <b-form-group label="Notes:" label-cols="2" label-for="pole-notes">
              <b-form-input id="pole-notes" plaintext v-model="sentinel.pole.notes"></b-form-input>
            </b-form-group>
          </b-form-group>
        </tab-content>
      </form-wizard>
    </b-container>
  </b-modal>
</template>

<script>
import $ from "jquery";
import VueBootstrapTypeahead from "vue-bootstrap-typeahead";
import MaskedInput from "vue-masked-input";
import {mapGetters, mapActions, mapState} from "vuex";
import { isEmpty, padStart } from "lodash";
import isNumberOrHyphenTextField from "@/core/isNumberOrHyphenTextFieldHelper";
import {circuitService} from "@/_services";

export default {
  name: "InstallationModal",
  components: {
    VueBootstrapTypeahead,
    MaskedInput
  },
  props: {
    title: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    poleToInstallDevice: {
      type: Object
    },
    deviceToInstall: {
      type: Object
    }
  },
  data() {
    return {
      maskedComponentKey: 0,
      min_serial_num_value: 1,
      max_serial_num_value: 16777215,
      selected_prefix: "279-",
      linesight_prefixes: ["279-", "162-"],
      sentinelSearch: "",
      poleSearch: "",
      stepOne: {
        valid: true
      },
      stepTwo: {
        valid: true,
        enableCreate: false
      },
      sentinel: {
        pole: null
      },
      newSentinel: {
        pole: {
          coordinates: {}
        },
        product_serial_num: ""
      },
      disable_new_device_input: false,
      disable_existing_device_input: false,
      is_serial_valid: true,
      polesWithNoDeviceInstalled: [],
      are_devices_loaded: false
    };
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    ...mapActions("device", ["findAll", "installDevice"]),
    isNumberOrHyphen: isNumberOrHyphenTextField,
    validateFirstTab: function() {
      this.trimMask();
      this.stepOne.valid = !this.isFirstTabInvalid;
      this.is_serial_valid = true
      if(!isEmpty(this.newSentinel.product_serial_num) && (!this.linesight_prefixes.includes(this.newSentinel.product_serial_num))) {
        this.newSentinel.product_serial_num = this.newSentinelFullSerial;
      }

      if (this.newSentinel.product_serial_num) {
        let sliced_serial = this.newSentinel.product_serial_num.slice(-8)
        sliced_serial = parseInt(sliced_serial, 10)

        if ((sliced_serial > this.max_serial_num_value) || (this.min_serial_num_value > sliced_serial)) {
          this.is_serial_valid = false
          this.stepOne.valid = false
        }
      }
      return this.stepOne.valid;
    },
    validateSecondTab: function() {
      if (!this.sentinel.pole && !this.newSentinel.pole.pole_substation_number) {
        this.stepTwo.valid = false;
        return false;
      }
      this.stepTwo.valid = true;
      return true;
    },
    selectDevice: function($event) {
      this.sentinel = JSON.parse(JSON.stringify($event));
      this.sentinel.pole = null;
      this.$refs.newSentinelInput.$refs.input.value = null;
      this.newSentinel.product_serial_num = null;
      if (!isEmpty(this.poleToInstallDevice)) {
        this.selectPole(this.poleToInstallDevice);
      }
      this.stepOne.valid = true;
    },
    selectNewDevice: function(event) {
      this.newSentinel.product_serial_num = this.selected_prefix + event
      this.is_serial_valid = true

      if (("162-________" != event || "279-________" != event) && event.length > 0) {
        this.disableExistingDeviceInput();
      } else {
        this.enableExistingDeviceInput();
      }
      this.sentinel.product_serial_num = null;
      if (this.$refs.deviceSearchTypeAhead != null) {
        this.$refs.deviceSearchTypeAhead.inputValue = "";
      }
      this.stepOne.valid = true;
    },
    enableExistingDeviceInput: function() {
      $(".deviceSearchInputTypeAhead").prop("disabled", false);
      this.disable_existing_device_input = false;
    },
    disableExistingDeviceInput: function() {
      $(".deviceSearchInputTypeAhead").prop("disabled", true);
      this.disable_existing_device_input = true;
    },
    selectPole: function($event) {
      this.sentinel.pole = JSON.parse(JSON.stringify($event));
      delete this.sentinel.pole.device;
    },
    handleAutocompleteInput: function($event) {
      this.disable_new_device_input =
        JSON.parse(JSON.stringify($event)).length > 0;
    },
    trimMask: function() {
      if (this.newSentinel.product_serial_num) {
        this.newSentinel.product_serial_num = this.newSentinel.product_serial_num.replace(
          /_/g,
          ""
        );
      }
    },
    clearSentinelSearch: function() {
      if (!isEmpty(this.$refs.deviceSearchTypeAhead)) {
        this.$refs.deviceSearchTypeAhead.inputValue = "";
      }
      this.sentinel = {};
      this.sentinelSearch = "";
      this.disable_new_device_input = false;
      if (this.poleToInstallDevice) {
        this.sentinel.pole = this.poleToInstallDevice;
      }
    },
    clearPoleSearch: function() {
      if (!isEmpty(this.$refs.poleSearchTypeAhead)) {
        this.$refs.poleSearchTypeAhead.inputValue = "";
      }
      this.poleSearch = "";
      this.sentinel.pole = null;
    },
    clearNewDeviceInput: function() {
      $("#newSentinelInput").val("");
      this.$refs.newSentinelInput.maskCore.setValue("");
      this.$refs.newSentinelInput.focusOut();
      this.enableExistingDeviceInput();
    },
    deviceLocationUrl: function(lat, long) {
      return "http://maps.google.com/?q=" + lat + "," + long;
    },
    callInstallDevice: function() {
      if (
        !this.sentinel.product_serial_num ||
        (!this.sentinel.product_serial_num &&
          this.sentinel.product_serial_num !=
            this.newSentinel.product_serial_num)
      ) {
        this.sentinel.product_serial_num = this.newSentinel.product_serial_num;
      }
      if (!this.sentinel.pole) {
        this.sentinel.pole = this.newSentinel.pole;
      }
      this.installDevice(this.sentinel)
        .then(() => {
          this.success(
            "Successfully installed LineSIGHT " +
              this.sentinel.product_serial_num
          );
          this.resetModal();
          this.findAll(this.hideInactiveCircuits);
          this.$refs.deviceInstallModal.hide();
          this.$emit("updateCircuit")
        })
        .catch(error => {
          let errResp = error.response;
          let errorCode = errResp && errResp.status;
          let errorMessage;
          if (errorCode >= 400 && errorCode < 500) {
            errorMessage = errResp && errResp.data && errResp.data.message;
          } else {
            errorMessage = "Something has went wrong, please contact support.";
          }
          this.error(errorMessage);
        });
    },
    async initialiseModal() {
      this.are_devices_loaded = false

      this.polesWithNoDeviceInstalled = await circuitService.polesWithNoDeviceInstalled()
      this.findAll(false)
      if (!isEmpty(this.deviceToInstall)) {
        this.selectDevice(this.deviceToInstall)
        this.sentinelSearch = this.sentinel.product_serial_num
      }

      if (!isEmpty(this.poleToInstallDevice)) {
        this.selectPole(this.poleToInstallDevice)
      }
      this.are_devices_loaded = true
    },
    resetModal: function() {
      this.are_devices_loaded = false
      this.clearPoleSearch();
      this.clearSentinelSearch();
      this.sentinel = {
        pole: null
      };
      this.newSentinel = {
        pole: {
          coordinates: {}
        },
        product_serial_num: ""
      };
      this.stepOne.valid = true;
      this.stepTwo.valid = true;
      this.$refs.newSentinelInput.$refs.input.value = null;
      this.forceRerender();
      this.$emit("modalReset");
      this.$refs.deviceInstallFormWizard.reset();
    },
    forceRerender() {
      this.maskedComponentKey += 1;
    },
    isVariableEmpty(variable) {
      return isEmpty(variable);
    }
  },
  computed: {
    ...mapGetters("device", ["devicesNotInstalled", "devicesNotInstalledSerials", "existingDeviceSerials"]),
    ...mapState("layout", {
      hideInactiveCircuits: state => state.hideInactiveCircuits
    }),
    isNewDeviceInputDisabled() {
      return this.disable_new_device_input;
    },
    isExistedDeviceInputDisabled() {
      return this.disable_existing_device_input;
    },
    inputsBothEmpty() {
      return (isEmpty(this.newSentinel.product_serial_num) || this.linesight_prefixes.includes(this.newSentinel.product_serial_num)) &&
        isEmpty(this.sentinelSearch);
    },
    isNewDeviceSerialSameAsExisting() {
      return this.existingDeviceSerials.includes(this.newSentinelFullSerial);
    },
    existingDeviceInputMatchesExistingDevice() {
      return this.devicesNotInstalledSerials.includes(this.sentinelSearch);
    },
    isValidNewDeviceSerial() {
      return !isEmpty(this.newSentinel.product_serial_num) &&
      !this.isNewDeviceSerialSameAsExisting;
    },
    showCreateDeviceError() {
      return !this.stepOne.valid &&
      !this.isValidNewDeviceSerial &&
      !isEmpty(this.newSentinel.product_serial_num);
    },
    showExistDeviceError() {
      return !this.stepOne.valid &&
      ((!isEmpty(this.sentinelSearch) && !this.esentinelSearchxistingDeviceInputMatchesExistingDevice) ||
      (!isEmpty(this.sentinelSearch) && (this.sentinelSearch !=this.sentinel.product_serial_num)));
    },
    isFirstTabInvalid() {
      return (this.inputsBothEmpty ||
      (!isEmpty(this.newSentinel.product_serial_num) && this.isNewDeviceSerialSameAsExisting) ||
      (!isEmpty(this.sentinelSearch) && !this.existingDeviceInputMatchesExistingDevice) ||
      (!isEmpty(this.sentinelSearch) && (this.sentinelSearch !=this.sentinel.product_serial_num)));
    },
    newSentinelSerialPartial() {
      return this.newSentinel.product_serial_num.replace(/_/g,"" ).substring(
          this.newSentinel.product_serial_num.lastIndexOf("-") + 1
        );
    },
    newSentinelFullSerial() {
      return this.selected_prefix + padStart(this.newSentinelSerialPartial, 8, "0");
  }
  }
};
</script>

<style src="./InstallationModal.scss" lang="scss" scoped/>
